import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Search as SearchIcon, MapPin, ArrowLeft, Star, Clock, Users, CheckCircle,
  Calendar, ArrowRight, RefreshCw, Shield, AlertCircle, Upload, X, CreditCard, QrCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { searchProviders, assignProvider, uploadBookingScreenshot, createPaymentOrder } from '../services/api';
import { SERVICES, getServiceById } from '../data/services';
import { toast } from 'sonner';
import { UploadIdProof } from '../components/UploadIdProof';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const BASE_RATE = 1000;

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser, token } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [hours, setHours] = useState(1);
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cashfree');
  const [prepaidBooking, setPrepaidBooking] = useState(null);
  const [assignModal, setAssignModal] = useState({ open: false, provider: null });
  const [assignNotes, setAssignNotes] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const fileInputRef = useRef(null);

  const rebookId = searchParams.get('rebookId');

  useEffect(() => {
    if (!token) navigate('/auth');
    const paymentStatus = searchParams.get('payment_status');
    const bookingId = searchParams.get('booking_id');
    if (paymentStatus === 'success' && bookingId) {
      setPrepaidBooking({ booking_id: bookingId });
      setSelectedService(searchParams.get('service') || '');
      setCurrentStep(4);
      toast.success('Payment successful! Now search for a provider.');
      return;
    }
    if (rebookId && selectedService) {
      setPrepaidBooking({ booking_id: rebookId });
      setCurrentStep(4);
    }
    // searchParams is read imperatively above; we intentionally don't re-run
    // when its reference changes (would cause infinite loop with replace).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, rebookId, selectedService]);

  const service = getServiceById(selectedService);
  const baseAmount = BASE_RATE * hours;
  const gstAmount = Math.round(baseAmount * 0.18);
  const totalAmount = baseAmount + gstAmount;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    setScreenshot(file);
    const reader = new FileReader();
    reader.onloadend = () => setScreenshotPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCashfreePayment = async () => {
    setPaymentLoading(true);
    try {
      const res = await createPaymentOrder({
        type: 'booking_prepay',
        service_id: selectedService,
        hours: hours,
        date: bookingDate,
        time: bookingTime
      });
      if (res.data.payment_session_id) {
        const cashfree = window.Cashfree({ mode: res.data.cashfree_mode || 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment gateway error. Try QR code option.');
      setPaymentLoading(false);
    }
  };

  const handleQRPayment = async () => {
    if (!screenshot) { toast.error('Please upload payment screenshot'); return; }
    setPaymentLoading(true);
    try {
      const res = await uploadBookingScreenshot(selectedService, hours, bookingDate, bookingTime, screenshot);
      const conf = res.data.verification?.confidence;
      setPrepaidBooking({ booking_id: res.data.booking_id, total_amount: res.data.total_amount });
      toast.success(`Payment verified (${conf} confidence)! Now search for a provider.`);
      setCurrentStep(4);
      setScreenshot(null);
      setScreenshotPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment verification failed. Please upload a valid screenshot.');
    } finally { setPaymentLoading(false); }
  };

  const handleSearch = async () => {
    if (!city && !pincode) { toast.error('Enter city or pincode'); return; }
    setLoading(true);
    try {
      const response = await searchProviders({
        service_id: selectedService,
        city: city || undefined,
        pincode: pincode || undefined
      });
      setProviders(response.data.providers);
      if (response.data.providers.length === 0) toast.info('No providers found. Try a different location.');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Search failed';
      if (status === 403 && /id proof/i.test(detail)) {
        toast.error('Please upload a valid ID proof to view providers.');
        await refreshUser?.();
        return;
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const handleAssignProvider = async () => {
    if (!prepaidBooking) return;
    setAssignLoading(true);
    try {
      await assignProvider(prepaidBooking.booking_id, {
        provider_id: assignModal.provider.id,
        notes: assignNotes
      });
      toast.success(`Booking sent to ${assignModal.provider.name}!`);
      setAssignModal({ open: false, provider: null });
      setAssignNotes('');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign provider');
    } finally { setAssignLoading(false); }
  };

  // Step 1: Choose Service (with highlighting)
  const renderStep1 = () => (
    <div className="animate-fade-in-up">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-amber-200">Step 1 of 4</div>
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Choose a Service</h2>
        <p className="text-gray-600">Select the type of service you need</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          const isSelected = selectedService === s.id;
          return (
            <div key={s.id} onClick={() => setSelectedService(s.id)}
              className={`cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                isSelected 
                  ? 'ring-3 ring-amber-500 shadow-lg shadow-amber-500/20 scale-[1.03]' 
                  : 'hover:shadow-md hover:scale-[1.01]'
              }`}
              data-testid={`service-option-${s.id}`}>
              <div className={`${s.color} p-4 h-full flex flex-col items-center justify-center min-h-[120px] relative`}>
                {isSelected && (
                  <div className="absolute top-2 right-2 bg-white rounded-full p-0.5">
                    <CheckCircle className="w-5 h-5 text-amber-500" />
                  </div>
                )}
                <Icon className="w-8 h-8 text-white mb-2" />
                <span className="font-medium text-white text-sm text-center">{s.name}</span>
                <div className="mt-2 bg-white/20 px-2 py-0.5 rounded-full text-xs text-white">Rs.{BASE_RATE}/hr</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex justify-end">
        <Button onClick={() => setCurrentStep(2)} disabled={!selectedService} className="btn-premium" data-testid="step1-continue">
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Step 2: Date, Time, Hours
  const renderStep2 = () => (
    <div className="animate-fade-in-up">
      <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"><ArrowLeft className="w-4 h-4" /> Back to Services</button>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-amber-200">Step 2 of 4</div>
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Select Date & Duration</h2>
      </div>
      <div className="max-w-md mx-auto space-y-6">
        <div className="premium-card p-4 flex items-center gap-4">
          {service && (<><div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center text-white`}><service.icon className="w-7 h-7" /></div><div><h3 className="font-medium text-gray-900">{service.name}</h3><p className="text-sm text-gray-500">{service.description}</p></div></>)}
        </div>
        <div><Label>Date</Label><Input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} className="input-premium mt-2" min={new Date().toISOString().split('T')[0]} data-testid="booking-date-input" /></div>
        <div><Label>Time</Label><Input type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="input-premium mt-2" data-testid="booking-time-input" /></div>
        <div>
          <Label>Duration (Hours)</Label>
          <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v))}>
            <SelectTrigger className="input-premium mt-2" data-testid="hours-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1,2,3,4,5,6,7,8].map(h => <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => setCurrentStep(3)} disabled={!bookingDate || !bookingTime} className="btn-premium w-full" data-testid="step2-continue">
          Proceed to Payment <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Step 3: Payment — Cashfree (primary) + QR (backup)
  const renderStep3 = () => (
    <div className="animate-fade-in-up">
      <button onClick={() => setCurrentStep(2)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-amber-200">Step 3 of 4</div>
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Complete Payment</h2>
      </div>
      <div className="max-w-md mx-auto">
        {/* Order Summary */}
        <div className="premium-card p-6 mb-5">
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
            {service && (<><div className={`w-12 h-12 ${service.color} rounded-xl flex items-center justify-center text-white`}><service.icon className="w-6 h-6" /></div><div><h3 className="font-medium text-gray-900">{service.name}</h3><p className="text-sm text-gray-500">{bookingDate} | {bookingTime} | {hours} hr{hours > 1 ? 's' : ''}</p></div></>)}
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span className="text-gray-600">Rate</span><span>Rs.{BASE_RATE}/hr</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">Duration</span><span>{hours} hour{hours > 1 ? 's' : ''}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span>Rs.{baseAmount.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">GST (18%)</span><span>Rs.{gstAmount.toLocaleString('en-IN')}</span></div>
            <div className="border-t border-gray-200 pt-2 flex justify-between font-bold">
              <span>Total</span><span className="text-amber-600 text-xl">Rs.{totalAmount.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Payment Method Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5">
          <button onClick={() => setPaymentMethod('cashfree')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'cashfree' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`} data-testid="service-method-cashfree">
            <CreditCard className="w-4 h-4" /> Pay Online
          </button>
          <button onClick={() => setPaymentMethod('qr')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'qr' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`} data-testid="service-method-qr">
            <QrCode className="w-4 h-4" /> QR Code
          </button>
        </div>

        {paymentMethod === 'cashfree' ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">You'll be redirected to a secure payment page. After payment, return here to search for providers.</p>
              </div>
            </div>
            <Button onClick={handleCashfreePayment} disabled={paymentLoading} className="w-full text-base py-6 rounded-xl font-semibold bg-green-600 hover:bg-green-700 text-white" data-testid="cashfree-booking-pay-btn">
              {paymentLoading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Redirecting...</> : <><CreditCard className="w-5 h-5 mr-2" /> Pay Rs.{totalAmount.toLocaleString('en-IN')} Online</>}
            </Button>
          </>
        ) : (
          <>
            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
              <p className="text-sm font-medium text-gray-700 mb-3">Scan QR code to pay via UPI</p>
              <img src="/payment-qr.jpeg" alt="Payment QR" className="w-48 h-48 mx-auto rounded-lg object-contain border border-gray-200" data-testid="service-payment-qr" />
              <p className="text-xs text-gray-500 mt-2">Pay to: SET INDIA BUSINESS PVT LTD</p>
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                <p className="text-amber-800 font-bold text-lg" data-testid="service-payment-amount">Pay Exactly: Rs.{totalAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Upload Payment Screenshot *</p>
              {screenshotPreview ? (
                <div className="relative">
                  <img src={screenshotPreview} alt="Screenshot" className="w-full h-40 object-contain rounded-xl border border-gray-200 bg-gray-50" />
                  <button onClick={() => { setScreenshot(null); setScreenshotPreview(null); }} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-5 text-center hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer" data-testid="upload-booking-screenshot-btn">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 font-medium text-sm">Click to upload screenshot</p>
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            </div>
            <Button onClick={handleQRPayment} disabled={paymentLoading || !screenshot} className={`w-full text-base py-6 rounded-xl font-semibold ${screenshot ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`} data-testid="confirm-booking-payment-btn">
              {paymentLoading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Verifying...</> : <><CheckCircle className="w-5 h-5 mr-2" /> Verify & Confirm</>}
            </Button>
            <p className="text-center text-xs text-gray-400 mt-2">AI verifies your payment before booking</p>
          </>
        )}
      </div>
    </div>
  );

  // Step 4: Search & Select Provider
  const renderStep4 = () => {
    // Hirer-side ID-proof gate: backend will 403 search/providers if missing.
    // Show the upload card instead of the search form so user can complete it.
    const needsIdProof = !!user && (user.id_proof_required && !user.id_proof_path);
    return (
    <div className="animate-fade-in-up">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-green-200">
          <CheckCircle className="w-4 h-4" /> Payment Confirmed — Step 4 of 4
        </div>
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Find Your Provider</h2>
        <p className="text-gray-600">Search by city or pincode</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {needsIdProof && (
          <div className="mb-6">
            <UploadIdProof
              uploaded={false}
              required
              onUploaded={refreshUser}
            />
          </div>
        )}

        <div className={`premium-card p-4 mb-6 flex flex-wrap items-center gap-4 ${needsIdProof ? 'opacity-60 pointer-events-none' : ''}`}>
          {service && <div className={`w-10 h-10 ${service.color} rounded-lg flex items-center justify-center text-white`}><service.icon className="w-5 h-5" /></div>}
          <div className="flex-1">
            <span className="font-medium">{service?.name}</span>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-gray-600">{bookingDate} | {bookingTime}</span>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-amber-600 font-medium">{hours} hr{hours > 1 ? 's' : ''}</span>
          </div>
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">Paid</span>
        </div>

        <div className={`grid md:grid-cols-2 gap-4 mb-6 ${needsIdProof ? 'opacity-60 pointer-events-none' : ''}`}>
          <div><Label>City</Label><Input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g., Mumbai, Delhi" className="input-premium mt-2" data-testid="city-input" /></div>
          <div><Label>Or Pincode</Label><Input type="text" maxLength={6} value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))} placeholder="e.g., 400001" className="input-premium mt-2" data-testid="pincode-input" /></div>
        </div>

        <Button onClick={handleSearch} disabled={loading || needsIdProof} className="btn-premium w-full mb-8" data-testid="search-btn">
          {loading ? <div className="spinner" /> : needsIdProof ? <>Upload ID Proof to Continue</> : <><SearchIcon className="w-4 h-4 mr-2" /> Search Providers</>}
        </Button>

        {providers.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Found {providers.length} provider{providers.length > 1 ? 's' : ''}</h3>
            <div className="space-y-4">
              {providers.map((provider) => (
                <div key={provider.id} className="profile-card" data-testid={`provider-card-${provider.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {provider.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900 text-lg cursor-pointer hover:text-amber-600"
                            onClick={() => navigate(`/provider/${provider.id}`)} data-testid={`view-provider-${provider.id}`}>{provider.name}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <MapPin className="w-4 h-4" /><span>{provider.city || 'N/A'} {provider.pincode && `- ${provider.pincode}`}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            <span className="text-sm font-medium">{provider.avg_rating || 'New'}</span>
                            {provider.total_reviews > 0 && <span className="text-xs text-gray-400">({provider.total_reviews} reviews)</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-amber-600">Rs.{provider.hourly_rate || BASE_RATE}</p>
                          <p className="text-sm text-gray-500">per hour</p>
                        </div>
                      </div>
                      {provider.bio && <p className="text-gray-600 text-sm mt-3 line-clamp-2">{provider.bio}</p>}
                      <div className="mt-4 flex gap-2">
                        <Button variant="outline" onClick={() => navigate(`/provider/${provider.id}`)} className="rounded-full py-2 px-4 text-sm" data-testid={`profile-btn-${provider.id}`}>View Profile</Button>
                        <Button onClick={() => setAssignModal({ open: true, provider })} className="btn-premium py-2 px-4" data-testid={`select-provider-${provider.id}`}>Select & Book</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {providers.length === 0 && (city || pincode) && !loading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><Users className="w-8 h-8 text-gray-400" /></div>
            <h3 className="font-medium text-gray-700 mb-2">No providers found</h3>
            <p className="text-gray-500 mb-4">Try a different city or pincode</p>
            <Button onClick={() => { setCity(''); setPincode(''); setProviders([]); }} variant="outline" className="rounded-full"><RefreshCw className="w-4 h-4 mr-2" /> Clear Search</Button>
          </div>
        )}
      </div>
    </div>
    );
  };
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <header className="fixed top-0 w-full z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-testid="back-dashboard">
              <ArrowLeft className="w-5 h-5" /><span className="hidden sm:inline">Dashboard</span>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="HumanOnRent.AI" className="w-9 h-9 rounded-xl" />
              <span className="font-bold text-lg bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent hidden sm:block">HumanOnRent.AI</span>
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-28 pb-12">
        <div className="flex items-center justify-center gap-2 mb-12">
          {[{ num: 1, label: 'Service' }, { num: 2, label: 'Date' }, { num: 3, label: 'Payment' }, { num: 4, label: 'Book' }].map((step) => (
            <div key={step.num} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  currentStep >= step.num ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>{currentStep > step.num ? <CheckCircle className="w-4 h-4" /> : step.num}</div>
                <span className="text-xs text-gray-500 mt-1">{step.label}</span>
              </div>
              {step.num < 4 && <div className={`w-10 h-0.5 mx-1 mb-5 ${currentStep > step.num ? 'bg-amber-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      <Dialog open={assignModal.open} onOpenChange={(open) => setAssignModal({ open, provider: null })}>
        <DialogContent className="premium-card sm:max-w-md">
          <DialogHeader><DialogTitle>Confirm Provider Selection</DialogTitle></DialogHeader>
          {assignModal.provider && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white text-xl font-bold">
                  {assignModal.provider.name?.charAt(0)}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{assignModal.provider.name}</h4>
                  <p className="text-sm text-gray-500">{assignModal.provider.city} {assignModal.provider.pincode && `- ${assignModal.provider.pincode}`}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Service</span><span className="font-medium">{service?.name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Date & Time</span><span>{bookingDate} | {bookingTime}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Duration</span><span>{hours} hr{hours > 1 ? 's' : ''}</span></div>
              </div>
              <div>
                <Label>Notes for Provider (Optional)</Label>
                <Textarea value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} placeholder="Any special requirements..." className="input-premium mt-2 py-3" rows={3} data-testid="assign-notes-input" />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                <p className="text-sm text-blue-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> If declined, you get up to 2 more selections.</p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setAssignModal({ open: false, provider: null })} className="rounded-full">Cancel</Button>
                <Button onClick={handleAssignProvider} disabled={assignLoading} className="btn-premium" data-testid="confirm-assign-btn">
                  {assignLoading ? <div className="spinner" /> : 'Confirm & Send Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Search;
